import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Heart, Trash2, ShoppingCart, FileText, Plus, Minus } from "lucide-react";
import { useWishlistStore } from "@/stores/wishlistStore";
import { useCartStore } from "@/stores/cartStore";
import { formatPrice } from "@/lib/shopify";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { getLocalizedTitle, type Language } from "@/lib/productTranslations";
import { buildProductPath } from "@/lib/productUrl";

export const WishlistDrawer = () => {
  const { t, getLocalizedPath, language } = useLanguage();
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  
  const { 
    items, 
    isOpen,
    removeItem, 
    updateQuantity,
    clearWishlist,
    setOpen,
    getTotalItems,
  } = useWishlistStore();
  
  const { addItem: addToCart, setOpen: setCartOpen } = useCartStore();
  
  const totalItems = getTotalItems();

  const loadImageAsBase64 = (url: string): Promise<{ data: string; aspectRatio: number } | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const maxSize = 200;
          let width = img.width;
          let height = img.height;
          const aspectRatio = width / height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);
            resolve({ data: canvas.toDataURL('image/png'), aspectRatio });
          } else {
            resolve(null);
          }
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const handleExportPdf = async () => {
    if (items.length === 0) {
      toast.error("Wishlist is empty");
      return;
    }

    setIsExportingPdf(true);
    try {
      // Lazy-load heavy PDF libs only when user actually exports
      const [{ default: jsPDF }, { default: autoTable }, { ensurePdfUnicodeFont }] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
        import('@/lib/pdfFonts'),
      ]);
      const doc = new jsPDF();
      await ensurePdfUnicodeFont(doc);
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const footerLines = [
        'haus@royal-bathrooms.com',
        '+371 6737 6780',
        'Rīga, Cēsu iela 31 k-3, LV-1012',
        'Mon–Fri: 10:00–18:00 | Sat: 10:00–15:00 | Sun: Closed',
        'SIA "Royal Bathrooms"',
        'Reg. No.: 40103384776 | VAT No.: LV40103384776',
      ];
      const footerStartY = pageHeight - 28;
      const footerLineHeight = 4.2;
      const drawCenteredLine = (text: string, y: number) => {
        const x = Math.max((pageWidth - doc.getTextWidth(text)) / 2, 10);
        doc.text(text, x, y);
      };
      
      // Header
      doc.setFontSize(24);
      doc.setFont('NotoSans', 'bold');
      doc.text('HAUS Wishlist', pageWidth / 2, 20, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('NotoSans', 'normal');
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 28, { align: 'center' });
      doc.text(`Total items: ${items.length}`, pageWidth / 2, 34, { align: 'center' });
      doc.setTextColor(0);

      // Load images - prefer variantImage if available
      const imagePromises = items.map(async (item) => {
        const imageUrl = item.variantImage?.url || item.product.node.images?.edges?.[0]?.node?.url;
        if (imageUrl) {
          return await loadImageAsBase64(imageUrl);
        }
        return null;
      });
      
      const images = await Promise.all(imagePromises);

      // Prepare table data
      const tableData = items.map((item, index) => {
        const options = item.selectedOptions.map(opt => `${opt.name}: ${opt.value}`).join('\n');
        const lineTotal = (parseFloat(item.price.amount) * (item.quantity || 1)).toFixed(2);
        const localizedTitle = getLocalizedTitle(item.product.node, language as Language);
        
        // Check if item has a sale price (compareAtPrice > price)
        const hasSale = item.compareAtPrice && 
          parseFloat(item.compareAtPrice.amount) > parseFloat(item.price.amount);
        
        const priceDisplay = hasSale
          ? `${item.compareAtPrice!.amount} ${item.price.currencyCode}\n${item.price.amount} ${item.price.currencyCode} (SALE)`
          : `${item.price.amount} ${item.price.currencyCode}`;
        
        return [
          '', // Image placeholder
          `${localizedTitle}\n${item.variantTitle !== 'Default Title' ? options : ''}`,
          item.sku || 'N/A',
          String(item.quantity || 1),
          priceDisplay,
          `${lineTotal} ${item.price.currencyCode}`,
        ];
      });

      // Create table
      autoTable(doc, {
        startY: 42,
        head: [['Image', 'Product', 'SKU', 'Qty', 'Price', 'Total']],
        body: tableData,
        headStyles: {
          fillColor: [30, 30, 30],
          textColor: [255, 255, 255],
          font: 'NotoSans',
          fontStyle: 'bold',
          halign: 'center',
        },
        columnStyles: {
          0: { cellWidth: 22, halign: 'center', valign: 'middle' },
          1: { cellWidth: 55, textColor: [0, 90, 170] },
          2: { cellWidth: 28, halign: 'center' },
          3: { cellWidth: 15, halign: 'center' },
          4: { cellWidth: 28, halign: 'right' },
          5: { cellWidth: 28, halign: 'right' },
        },
        styles: {
          font: 'NotoSans',
          fontSize: 9,
          cellPadding: 4,
          valign: 'middle',
          overflow: 'linebreak',
        },
        margin: {
          bottom: 42,
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.index === 0) {
            const imgData = images[data.row.index];
            if (imgData) {
              const cellHeight = data.cell.height;
              const cellWidth = data.cell.width;
              const padding = 3;
              const maxW = cellWidth - padding * 2;
              const maxH = cellHeight - padding * 2;
              
              // Fit image maintaining aspect ratio
              let imgW, imgH;
              if (imgData.aspectRatio > 1) {
                imgW = Math.min(maxW, 20);
                imgH = imgW / imgData.aspectRatio;
              } else {
                imgH = Math.min(maxH, 20);
                imgW = imgH * imgData.aspectRatio;
              }
              if (imgW > maxW) { imgW = maxW; imgH = imgW / imgData.aspectRatio; }
              if (imgH > maxH) { imgH = maxH; imgW = imgH * imgData.aspectRatio; }
              
              const x = data.cell.x + (cellWidth - imgW) / 2;
              const y = data.cell.y + (cellHeight - imgH) / 2;
              try {
                doc.addImage(imgData.data, 'PNG', x, y, imgW, imgH);
              } catch (e) {
                // Skip if image fails
              }
            }
          }
          // Custom price rendering for sale items
          if (data.section === 'body' && data.column.index === 4) {
            const item = items[data.row.index];
            if (item?.compareAtPrice && parseFloat(item.compareAtPrice.amount) > parseFloat(item.price.amount)) {
              // Clear the default text by drawing a filled rect
              const fillColor = data.row.index % 2 === 0 ? [255, 255, 255] : [245, 245, 245];
              doc.setFillColor(fillColor[0], fillColor[1], fillColor[2]);
              doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
              
              const cellCenterX = data.cell.x + data.cell.width - 4;
              const cellCenterY = data.cell.y + data.cell.height / 2;
              
              // Regular price with strikethrough
              const regularText = `${item.compareAtPrice.amount} ${item.price.currencyCode}`;
              doc.setFontSize(8);
              doc.setFont('NotoSans', 'normal');
              doc.setTextColor(150);
              doc.text(regularText, cellCenterX, cellCenterY - 3, { align: 'right' });
              const textWidth = doc.getTextWidth(regularText);
              doc.setDrawColor(150);
              doc.setLineWidth(0.3);
              doc.line(cellCenterX - textWidth, cellCenterY - 3.5, cellCenterX, cellCenterY - 3.5);
              
              // Sale price in red bold
              const saleText = `${item.price.amount} ${item.price.currencyCode}`;
              doc.setFontSize(9);
              doc.setFont('NotoSans', 'bold');
              doc.setTextColor(200, 30, 30);
              doc.text(saleText, cellCenterX, cellCenterY + 4, { align: 'right' });
              
              // Reset
              doc.setTextColor(0);
              doc.setFont('NotoSans', 'normal');
            }
          }
          // Add clickable product link on product name column
          if (data.section === 'body' && data.column.index === 1) {
            const item = items[data.row.index];
            if (item) {
              const langPrefix = language === 'en' ? '/en' : language === 'lv' ? '/lv' : '/ru';
              const productPath = buildProductPath(item.product.node, { sku: item.sku || null });
              const productUrl = `https://www.haus.lv${langPrefix}${productPath}`;
              (doc as any).createAnnotation({
                type: 'link',
                bounds: {
                  x: data.cell.x,
                  y: data.cell.y,
                  w: data.cell.width,
                  h: data.cell.height,
                },
                contents: productUrl,
                open: false,
                url: productUrl,
              });
            }
          }
        },
        rowPageBreak: 'avoid',
        bodyStyles: {
          minCellHeight: 28,
        },
      });

      // Calculate totals
      const totalPrice = items.reduce((sum, item) => sum + parseFloat(item.price.amount) * (item.quantity || 1), 0);
      const totalQty = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
      const vatRate = 0.21;
      const vatAmount = totalPrice * vatRate / (1 + vatRate);
      
      // Add total at the end
      let finalY = (doc as any).lastAutoTable.finalY + 10;
      if (finalY > pageHeight - 42) {
        doc.addPage();
        finalY = 20;
      }
      const currency = items[0]?.price.currencyCode || 'EUR';
      doc.setFontSize(12);
      doc.setFont('NotoSans', 'bold');
      doc.text(`Items: ${totalQty}  |  Total: ${totalPrice.toFixed(2)} ${currency}`, pageWidth - 15, finalY, { align: 'right' });
      doc.setFontSize(9);
      doc.setFont('NotoSans', 'normal');
      doc.setTextColor(100);
      doc.text(`incl. VAT 21%: ${vatAmount.toFixed(2)} ${currency}`, pageWidth - 15, finalY + 6, { align: 'right' });
      doc.setTextColor(0);

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setFont('NotoSans', 'normal');
        doc.setTextColor(130);
        footerLines.forEach((line, index) => {
          drawCenteredLine(line, footerStartY + index * footerLineHeight);
        });
        drawCenteredLine(`Page ${i} of ${pageCount} | haus.lv`, pageHeight - 3.5);
      }

      // Download
      const date = new Date().toISOString().split('T')[0];
      doc.save(`wishlist_${date}.pdf`);
      
      toast.success("Wishlist PDF exported!");
    } catch (error) {
      console.error('PDF Export failed:', error);
      toast.error("Failed to export PDF");
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleAddToCart = (item: typeof items[0]) => {
    addToCart({
      product: item.product,
      variantId: item.variantId,
      variantTitle: item.variantTitle,
      price: item.price,
      quantity: item.quantity || 1,
      selectedOptions: item.selectedOptions,
    });
    toast.success("Added to cart!");
  };

  const handleAddAllToCart = () => {
    items.forEach(item => {
      addToCart({
        product: item.product,
        variantId: item.variantId,
        variantTitle: item.variantTitle,
        price: item.price,
        quantity: item.quantity || 1,
        selectedOptions: item.selectedOptions,
      });
    });
    toast.success(`Added ${items.length} items to cart!`);
    setCartOpen(true);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full bg-background border-l border-border">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-display text-2xl text-foreground flex items-center gap-2">
            <Heart className="h-6 w-6 text-red-500" />
            Wishlist
          </SheetTitle>
          <SheetDescription className="text-muted-foreground font-body">
            {totalItems === 0 
              ? "Your wishlist is empty" 
              : `${totalItems} ${totalItems === 1 ? 'item' : 'items'} saved`
            }
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-6">
                  <Heart className="h-10 w-10 text-muted-foreground" />
                </div>
                <p className="text-foreground font-semibold mb-2 font-display">Your wishlist is empty</p>
                <p className="text-sm text-muted-foreground font-body">
                  Save items you love by clicking the heart icon
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Action buttons */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportPdf}
                  disabled={isExportingPdf}
                  className="w-full"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {isExportingPdf ? "..." : "PDF"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddAllToCart}
                  disabled={isExportingPdf}
                  className="w-full"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Add All
                </Button>
              </div>

              {/* Scrollable items area */}
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div 
                      key={item.variantId} 
                      className="flex gap-4 p-4 rounded-2xl bg-card border border-border animate-fade-in"
                    >
                    {(() => {
                      const vendor = item.product.node.vendor?.toLowerCase() || '';
                      const needsWhiteBg = vendor.includes('deante') || vendor.includes('victoria');
                      return (
                        <Link 
                          to={getLocalizedPath(buildProductPath(item.product.node))}
                          onClick={() => setOpen(false)}
                          className={`w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 ${needsWhiteBg ? 'bg-white p-1' : 'bg-secondary'}`}
                        >
                          {(item.variantImage?.url || item.product.node.images?.edges?.[0]?.node?.url) && (
                            <img
                              src={item.variantImage?.url || item.product.node.images.edges[0].node.url}
                              alt={item.variantImage?.altText || item.product.node.title}
                              className="w-full h-full object-contain hover:scale-105 transition-transform"
                            />
                          )}
                        </Link>
                      );
                    })()}
                      
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={getLocalizedPath(buildProductPath(item.product.node))}
                          onClick={() => setOpen(false)}
                        >
                          <h4 className="font-semibold text-sm line-clamp-2 text-foreground font-display hover:text-primary transition-colors">
                            {getLocalizedTitle(item.product.node, language as Language)}
                          </h4>
                        </Link>
                        {item.variantTitle !== 'Default Title' && (
                          <p className="text-xs text-muted-foreground mt-0.5 font-body">
                            {item.selectedOptions.map(option => option.value).join(' • ')}
                          </p>
                        )}
                        {item.sku && (
                          <p className="text-xs text-muted-foreground mt-0.5 font-body">
                            SKU: {item.sku}
                          </p>
                        )}
                        <p className="font-display font-bold text-copper mt-1">
                          {formatPrice(item.price.amount, item.price.currencyCode)}
                        </p>
                        {/* Quantity controls */}
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => updateQuantity(item.variantId, (item.quantity || 1) - 1)}
                            disabled={(item.quantity || 1) <= 1}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="text-sm font-semibold w-6 text-center">{item.quantity || 1}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => updateQuantity(item.variantId, (item.quantity || 1) + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => removeItem(item.variantId)}
                          title="Remove from wishlist"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleAddToCart(item)}
                          title="Add to cart"
                        >
                          <ShoppingCart className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Footer with clear button */}
              <div className="flex-shrink-0 pt-6 border-t border-border bg-background">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    clearWishlist();
                    toast.success("Wishlist cleared");
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Wishlist
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
